import { useState } from "react";
import { Check, Spinner, WarningCircle } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useLocation } from "react-router-dom";

import { userCourseAccessQueryKeys, useUserCourseAccess } from "@/hooks";
import i18n from "@/i18n";
import type { ModalProps } from "@/shared.types";
import type { UserCourseAccess } from "@/types";
import {
  Button,
  Circle,
  errorToast,
  Heading,
  Modal,
  useToastStore,
} from "@/ui";
import { formatDateToDDMMYYYY } from "@/utils/dates";

interface LocationState {
  userCourseAccess: UserCourseAccess;
}

export const ManageUserCourseAccessModal = ({ show, onClose }: ModalProps) => {
  const pushToast = useToastStore.getState().pushToast;
  const location = useLocation();
  const state = location.state as LocationState;

  const access = state?.userCourseAccess;
  const existingStart = access?.access_start_date
    ? (new Date(access.access_start_date).toISOString().split("T")[0] ?? "")
    : "";
  const existingEnd = access?.access_end_date
    ? (new Date(access.access_end_date).toISOString().split("T")[0] ?? "")
    : "";
  const isApprovedUser =
    access?.status === "paid" || access?.status === "pending payment";

  const [startDate, setStartDate] = useState<string>(existingStart);
  const [endDate, setEndDate] = useState<string>(existingEnd);
  const [indefinite, setIndefinite] = useState<boolean>(
    isApprovedUser && !existingEnd,
  );

  const queryClient = useQueryClient();
  const { useUpdateUserCourseAccess } = useUserCourseAccess();
  const { mutate: updateUserCourseAccess, isPending } =
    useUpdateUserCourseAccess();

  if (!access) {
    return null;
  }

  const { id: userCourseAccessId, status } = access;
  const first_name = access.user?.first_name ?? "";
  const name = access.course?.name ?? "";

  const handleAction = (
    targetStatus: "pending payment" | "declined" | "paid" | "withdrawn",
  ) => {
    if (!userCourseAccessId) return;

    updateUserCourseAccess(
      {
        userCourseAccessId,
        status: targetStatus,
        accessStartDate: startDate || null,
        accessEndDate: indefinite ? null : endDate || null,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: userCourseAccessQueryKeys.useUserCourseAccess(),
            exact: false,
          });

          const toastMap: Record<
            string,
            { type: "success" | "warning" | "error"; title: string }
          > = {
            declined: {
              type: "warning",
              title: i18n.t("materials.request_successfully_declined"),
            },
            withdrawn: {
              type: "warning",
              title: i18n.t("materials.access_successfully_withdrawn"),
            },
            paid: {
              type: "success",
              title: i18n.t("materials.access_successfully_updated"),
            },
            "pending payment": {
              type: "success",
              title: i18n.t("materials.request_successfully_approved"),
            },
          };

          const toast = toastMap[targetStatus];
          if (toast) {
            void pushToast({
              type: toast.type,
              title: toast.title,
              icon: <Check size={18} />,
            });
          }
          onClose();
        },
        onError: (err) => {
          errorToast(err);
        },
      },
    );
  };

  return (
    <Modal
      show={show}
      title={
        <div className="flex w-full flex-col gap-y-5 sm:w-[492px]">
          <Heading
            containerClassName="items-center"
            heading={`${t("materials.manage")} ${first_name}'s ${t("materials.access")}`}
            left={
              <Circle>
                <Check size={24} className="text-tertiary-800" />
              </Circle>
            }
          />

          {isApprovedUser ? (
            <p className="text-base font-normal text-primary-950">
              {t("materials.managing_access_for")} <b>{first_name}</b>{" "}
              {t("materials.to")} <b>{name}</b>.
            </p>
          ) : (
            <p className="text-base font-normal text-primary-950">
              {t("materials.do_you_want_to_approve")} <b>{first_name}</b>
              {"'s "}
              {t("materials.request_to")} <b>{name}</b>?
            </p>
          )}
        </div>
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-4 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-4">
          <p className="text-sm font-medium text-neutral-700">
            {t("materials.access_period")}
          </p>

          <div className="flex flex-col gap-y-3">
            <div className="flex flex-col gap-y-1">
              <label className="text-xs font-medium text-neutral-600">
                {t("materials.access_start_date")}{" "}
                <span className="font-normal text-neutral-400">
                  ({t("materials.optional")})
                </span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 placeholder:text-neutral-400 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600"
              />
            </div>

            <div className="flex flex-col gap-y-1">
              <label className="text-xs font-medium text-neutral-600">
                {t("materials.access_end_date")}
              </label>
              {indefinite ? (
                <div className="flex items-center rounded-xl border border-neutral-200 bg-white p-3">
                  <span className="text-sm italic text-neutral-500">
                    {t("materials.indefinite_access")}
                  </span>
                </div>
              ) : (
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 placeholder:text-neutral-400 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600"
                />
              )}

              <label className="mt-1 flex cursor-pointer items-center gap-x-2">
                <input
                  type="checkbox"
                  checked={indefinite}
                  onChange={(e) => {
                    setIndefinite(e.target.checked);
                    if (e.target.checked) setEndDate("");
                  }}
                  className="h-4 w-4 rounded border-neutral-400 accent-tertiary-600"
                />
                <span className="text-xs text-neutral-600">
                  {t("materials.no_expiry_date")}
                </span>
              </label>
            </div>
          </div>

          {(startDate || !indefinite) && (startDate || endDate) && (
            <p className="text-xs text-tertiary-700">
              {startDate && !endDate && indefinite
                ? `${t("materials.access_from")} ${formatDateToDDMMYYYY(new Date(startDate))} — ${t("materials.no_expiry")}`
                : startDate && endDate
                  ? `${formatDateToDDMMYYYY(new Date(startDate))} → ${formatDateToDDMMYYYY(new Date(endDate))}`
                  : endDate
                    ? `${t("materials.expires")} ${formatDateToDDMMYYYY(new Date(endDate))}`
                    : null}
            </p>
          )}
        </div>

        {isApprovedUser ? (
          <div className="flex flex-col gap-y-3">
            <Button
              onClick={() => handleAction(status as "paid" | "pending payment")}
              className="w-full"
              variant="filled"
              disabled={isPending}
            >
              {isPending ? (
                <Spinner className="h-5 w-5 animate-spin" />
              ) : (
                t("materials.save_changes")
              )}
            </Button>

            <div className="flex items-center gap-x-2 rounded-xl border border-error-200 bg-error-50 px-4 py-3">
              <WarningCircle size={16} className="shrink-0 text-error-600" />
              <p className="text-xs text-error-700">
                {t("materials.withdraw_warning")}
              </p>
            </div>

            <Button
              onClick={() => handleAction("withdrawn")}
              className="w-full"
              variant="outlined"
              disabled={isPending}
            >
              {isPending ? (
                <Spinner className="h-5 w-5 animate-spin" />
              ) : (
                t("materials.withdraw_access")
              )}
            </Button>
          </div>
        ) : (
          <div className="flex flex-row justify-center gap-x-2">
            <Button
              onClick={() => handleAction("declined")}
              className="flex-grow"
              variant="outlined"
              disabled={isPending}
            >
              {isPending ? (
                <Spinner className="h-5 w-5 animate-spin" />
              ) : (
                t("materials.decline")
              )}
            </Button>
            <Button
              onClick={() => handleAction("pending payment")}
              className="flex-grow"
              variant="filled"
              disabled={isPending}
            >
              {isPending ? (
                <Spinner className="h-5 w-5 animate-spin" />
              ) : (
                t("materials.approve")
              )}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
